// Data-driven world content: realms, campaign stages, skins, emotes, progression.
// Adding a realm = add an entry to REALMS + art under /art/realms/<id>/. Everything else derives from data.

import { BASE_SPEED } from './constants';

export interface RealmDef {
  id: string;
  /** i18n key prefix: realm.<id>.name / realm.<id>.lore */
  color: string;
  accent: string;
  /** Visual tint for the boss + map. */
  bg: string;
  bossKind: 'bouncer' | 'charger' | 'splitter' | 'hunter' | 'warper' | 'orbiter';
}

export const REALMS: RealmDef[] = [
  { id: 'verdant', color: '#3FBF6B', accent: '#B6F09C', bg: '#0E2A1A', bossKind: 'bouncer' },
  { id: 'ember', color: '#FF7A3D', accent: '#FFD27A', bg: '#2A120A', bossKind: 'charger' },
  { id: 'tidal', color: '#2E9BFF', accent: '#9FE3FF', bg: '#08203A', bossKind: 'splitter' },
  { id: 'frost', color: '#8FD8FF', accent: '#E8F7FF', bg: '#0F2236', bossKind: 'hunter' },
  { id: 'neon', color: '#FF3DC8', accent: '#7CF9FF', bg: '#16082A', bossKind: 'warper' },
  { id: 'astral', color: '#9B7BFF', accent: '#FFE48A', bg: '#0A0820', bossKind: 'orbiter' },
];

export const STAGES_PER_REALM = 4;

export interface StageDef {
  id: string; // e.g. "verdant-1"
  realm: RealmDef;
  realmIndex: number;
  index: number; // 0-based within realm
  global: number; // 0-based across campaign
  width: number;
  height: number;
  targetPct: number; // 0..1, of claimable area
  bossSpeed: number;
  minions: number;
  hunters: number;
  fuseDelay: number; // seconds outside before the fuse ignites
  fuseSpeed: number; // cells/sec along the trail
  parTime: number; // seconds for the time star
  lives: number;
  mural: string; // path relative to /art
  seed: number;
  /** Boss radius in cells (default 2.2). */
  bossRadius?: number;
}

export function buildStage(realmIndex: number, index: number): StageDef {
  const realm = REALMS[realmIndex];
  const d = realmIndex * STAGES_PER_REALM + index; // 0..23
  return {
    id: `${realm.id}-${index + 1}`,
    realm,
    realmIndex,
    index,
    global: d,
    width: 108,
    height: 72,
    targetPct: Math.min(0.85, 0.7 + d * 0.0065),
    bossSpeed: 5.5 + d * 0.16,
    minions: Math.min(7, 1 + Math.floor(d / 3)),
    hunters: realmIndex >= 3 ? 1 + Math.floor(index / 2) : realmIndex === 2 && index >= 2 ? 1 : 0,
    fuseDelay: Math.max(1.8, 4.2 - d * 0.09),
    fuseSpeed: BASE_SPEED * 0.5 + d * 0.06,
    parTime: 75 + d * 3,
    lives: 3,
    mural: `realms/${realm.id}/mural-${index + 1}.webp`,
    seed: 1000 + d * 7919,
  };
}

export const CAMPAIGN: StageDef[] = REALMS.flatMap((_, r) =>
  Array.from({ length: STAGES_PER_REALM }, (_, i) => buildStage(r, i)),
);

export function stageById(id: string): StageDef | undefined {
  return CAMPAIGN.find((s) => s.id === id);
}

/** Endless "Atlas Infinite" stage generator: difficulty keeps climbing, murals cycle through all realms. */
export function infiniteStage(level: number): StageDef {
  const realmIndex = level % REALMS.length;
  const base = buildStage(realmIndex, level % STAGES_PER_REALM);
  const d = 24 + level;
  return {
    ...base,
    id: `infinite-${level + 1}`,
    global: d,
    targetPct: Math.min(0.88, 0.8 + level * 0.004),
    bossSpeed: Math.min(13, 9 + level * 0.12),
    minions: Math.min(9, 5 + Math.floor(level / 4)),
    hunters: Math.min(4, 1 + Math.floor(level / 5)),
    fuseDelay: Math.max(1.5, 2.2 - level * 0.03),
    fuseSpeed: Math.min(BASE_SPEED * 0.9, BASE_SPEED * 0.6 + level * 0.08),
    parTime: 150,
    seed: 50000 + level * 104729,
  };
}

// ---------------------------------------------------------------- Skins

export type SkinPattern = 'solid' | 'stripes' | 'dots' | 'checker' | 'gradient' | 'stars' | 'rainbow' | 'metal';
export interface SkinDef {
  id: number;
  key: string; // i18n: skin.<key>
  color: string; // territory + body
  dark: string; // shadow / outline
  trail: string;
  pattern: SkinPattern;
  hat: 'explorer' | 'crown' | 'beanie' | 'halo' | 'horns' | 'none' | 'leaf' | 'antenna' | 'pumpkin';
  unlockLevel?: number;
  price?: number; // coins
  /** Unlocked only by a secret flag. */
  secret?: string;
}

export const SKINS: SkinDef[] = [
  { id: 0, key: 'mapi', color: '#19C3B5', dark: '#0B7F76', trail: '#7CF2E6', pattern: 'solid', hat: 'explorer' },
  { id: 1, key: 'coral', color: '#FF6B6B', dark: '#B33A3A', trail: '#FFB0B0', pattern: 'solid', hat: 'beanie' },
  { id: 2, key: 'sunny', color: '#FFC83D', dark: '#B8861A', trail: '#FFE59A', pattern: 'solid', hat: 'none' },
  { id: 3, key: 'grape', color: '#9B6BFF', dark: '#5E3BB8', trail: '#CDB6FF', pattern: 'solid', hat: 'antenna' },
  { id: 4, key: 'mint', color: '#4FE39A', dark: '#21935C', trail: '#A8F5CC', pattern: 'dots', hat: 'leaf', unlockLevel: 2 },
  { id: 5, key: 'sky', color: '#46A8FF', dark: '#1F63B0', trail: '#A6D5FF', pattern: 'stripes', hat: 'beanie', unlockLevel: 3 },
  { id: 6, key: 'peach', color: '#FF9F6E', dark: '#B8623A', trail: '#FFD0B5', pattern: 'dots', hat: 'none', unlockLevel: 4 },
  { id: 7, key: 'bubblegum', color: '#FF7AD9', dark: '#B03E91', trail: '#FFC2EE', pattern: 'checker', hat: 'antenna', unlockLevel: 5 },
  { id: 8, key: 'lime', color: '#B4F03C', dark: '#6E9A16', trail: '#DDF8A4', pattern: 'stripes', hat: 'leaf', unlockLevel: 6 },
  { id: 9, key: 'ocean', color: '#2BD2FF', dark: '#127A99', trail: '#9BEBFF', pattern: 'gradient', hat: 'explorer', unlockLevel: 8 },
  { id: 10, key: 'ember', color: '#FF5A1F', dark: '#A12E08', trail: '#FFAD86', pattern: 'gradient', hat: 'horns', price: 400 },
  { id: 11, key: 'frost', color: '#BFEFFF', dark: '#6BA9C2', trail: '#E9FAFF', pattern: 'stars', hat: 'beanie', price: 400 },
  { id: 12, key: 'neon', color: '#F23DFF', dark: '#8A12A0', trail: '#FBA8FF', pattern: 'checker', hat: 'antenna', price: 600 },
  { id: 13, key: 'forest', color: '#2E9E5B', dark: '#175C33', trail: '#93D9AE', pattern: 'dots', hat: 'leaf', price: 600 },
  { id: 14, key: 'royal', color: '#FFD447', dark: '#A8861A', trail: '#FFF0A8', pattern: 'stars', hat: 'crown', unlockLevel: 12 },
  { id: 15, key: 'angel', color: '#FFFFFF', dark: '#A7B3C9', trail: '#FFFFFF', pattern: 'solid', hat: 'halo', price: 900 },
  { id: 16, key: 'void', color: '#3B3355', dark: '#17122A', trail: '#8D7FBF', pattern: 'stars', hat: 'horns', unlockLevel: 15 },
  { id: 17, key: 'cosmo', color: '#6C5BFF', dark: '#3524B3', trail: '#B9B0FF', pattern: 'stars', hat: 'antenna', price: 1200 },
  { id: 18, key: 'candy', color: '#FF4F8B', dark: '#A81F52', trail: '#FFB3CD', pattern: 'stripes', hat: 'crown', price: 1500 },
  { id: 19, key: 'aurora', color: '#38F5C9', dark: '#159478', trail: '#B0FFF0', pattern: 'gradient', hat: 'halo', unlockLevel: 20 },
  { id: 20, key: 'prism', color: '#FF6BD6', dark: '#7A3CFF', trail: '#FFD1F5', pattern: 'rainbow', hat: 'explorer', secret: 'egg_konami' },
  { id: 21, key: 'golden', color: '#FFCF33', dark: '#9C6B00', trail: '#FFF0A0', pattern: 'metal', hat: 'crown', secret: 'egg_hunter' },
  { id: 22, key: 'pumpkin', color: '#FF8A1F', dark: '#9A3F00', trail: '#FFC48A', pattern: 'stripes', hat: 'pumpkin', secret: 'egg_pumpkin' },
];

export function skin(id: number): SkinDef {
  return SKINS[id] ?? SKINS[0];
}

/** Bots and unlocked-free palette. */
export const FREE_SKINS = SKINS.filter((s) => !s.price && !s.unlockLevel && !s.secret).map((s) => s.id);

export const EMOTES = ['wave', 'laugh', 'angry', 'cry', 'love', 'gg', 'cool', 'shock'] as const;
export type Emote = (typeof EMOTES)[number];
export const EMOTE_GLYPH: Record<Emote, string> = {
  wave: '👋', laugh: '😂', angry: '😤', cry: '😭', love: '😍', gg: '🤝', cool: '😎', shock: '😱',
};

// ---------------------------------------------------------------- Progression

export function xpForLevel(level: number): number {
  // XP needed to go from `level` to `level + 1`.
  return Math.round(120 + level * 80 + level * level * 6);
}

export function levelFromXp(totalXp: number): { level: number; into: number; need: number } {
  let level = 1;
  let rest = totalXp;
  while (rest >= xpForLevel(level) && level < 200) {
    rest -= xpForLevel(level);
    level++;
  }
  return { level, into: rest, need: xpForLevel(level) };
}

export interface MatchSummary {
  mode: string;
  maxPct: number; // 0..100
  kills: number;
  score: number;
  durationMs: number;
  rank?: number;
  won?: boolean;
}

export function rewardsForMatch(m: MatchSummary): { xp: number; coins: number } {
  const secs = Math.min(600, m.durationMs / 1000);
  const xp = Math.round(10 + m.maxPct * 4 + m.kills * 15 + secs * 0.3 + (m.won ? 60 : 0));
  const coins = Math.round(5 + m.maxPct * 1.2 + m.kills * 6 + (m.won ? 40 : 0));
  return { xp: Math.min(xp, 900), coins: Math.min(coins, 400) };
}

export function rewardsForStage(stars: number, firstClear: boolean): { xp: number; coins: number } {
  return { xp: 40 + stars * 30 + (firstClear ? 80 : 0), coins: 10 + stars * 10 + (firstClear ? 50 : 0) };
}

// ---------------------------------------------------------------- Daily missions

export interface MissionDef {
  key: string; // i18n mission.<key>, {n}
  stat: 'kills' | 'captures' | 'pct' | 'matches' | 'stars' | 'bigCapture' | 'minions';
  goal: number;
  reward: number;
}

export const MISSION_POOL: MissionDef[] = [
  { key: 'kills', stat: 'kills', goal: 5, reward: 80 },
  { key: 'kills', stat: 'kills', goal: 12, reward: 160 },
  { key: 'captures', stat: 'captures', goal: 30, reward: 70 },
  { key: 'pct', stat: 'pct', goal: 20, reward: 90 },
  { key: 'pct', stat: 'pct', goal: 40, reward: 180 },
  { key: 'matches', stat: 'matches', goal: 3, reward: 60 },
  { key: 'stars', stat: 'stars', goal: 5, reward: 120 },
  { key: 'bigCapture', stat: 'bigCapture', goal: 3, reward: 100 },
  { key: 'minions', stat: 'minions', goal: 6, reward: 90 },
];

/** Deterministic 3 missions for a given UTC day (yyyy-mm-dd). */
export function missionsForDay(day: string): MissionDef[] {
  let h = 0;
  for (const c of day) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const picked: MissionDef[] = [];
  const used = new Set<string>();
  let i = 0;
  while (picked.length < 3 && i < 50) {
    const m = MISSION_POOL[(h + i * 7) % MISSION_POOL.length];
    if (!used.has(m.stat)) {
      used.add(m.stat);
      picked.push(m);
    }
    i++;
  }
  return picked;
}

export function utcDay(d = new Date()): string {
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------- Retention: daily streak, scratch card, achievements, secrets

/** Coins for streak day 1..7 (cycle repeats; day 7 is the jackpot). */
export const DAILY_REWARDS = [50, 80, 120, 150, 200, 250, 600];

export function dailyReward(streak: number): number {
  return DAILY_REWARDS[(Math.max(1, streak) - 1) % DAILY_REWARDS.length];
}

/** Treasure Scratch prize table (weights). */
export const SCRATCH_PRIZES: { coins: number; weight: number }[] = [
  { coins: 20, weight: 30 }, { coins: 40, weight: 26 }, { coins: 60, weight: 18 }, { coins: 100, weight: 13 },
  { coins: 200, weight: 8 }, { coins: 500, weight: 4 }, { coins: 1000, weight: 1 },
];

export function rollScratch(r: number): number {
  const total = SCRATCH_PRIZES.reduce((s, p) => s + p.weight, 0);
  let x = r * total;
  for (const p of SCRATCH_PRIZES) {
    x -= p.weight;
    if (x < 0) return p.coins;
  }
  return SCRATCH_PRIZES[0].coins;
}

export interface FlagReward {
  coins: number;
  skin?: number;
  /** Only claimable in this UTC month (1-12). */
  month?: number;
  kind: 'achievement' | 'secret';
  /** Lifetime stat threshold for achievements (checked client-side, rewarded once server-side). */
  stat?: LifetimeStat;
  goal?: number;
  icon: string;
}

export type LifetimeStat = 'captures' | 'kills' | 'bestPct' | 'stars' | 'level' | 'streak' | 'matches' | 'megas' | 'minions' | 'clears' | 'eggs';

export const FLAGS: Record<string, FlagReward> = {
  // achievements
  ach_first_capture: { kind: 'achievement', stat: 'captures', goal: 1, coins: 30, icon: '🖌️' },
  ach_captures_100: { kind: 'achievement', stat: 'captures', goal: 100, coins: 150, icon: '🗺️' },
  ach_captures_1000: { kind: 'achievement', stat: 'captures', goal: 1000, coins: 600, icon: '🌍' },
  ach_kills_1: { kind: 'achievement', stat: 'kills', goal: 1, coins: 40, icon: '✂️' },
  ach_kills_50: { kind: 'achievement', stat: 'kills', goal: 50, coins: 250, icon: '⚔️' },
  ach_kills_500: { kind: 'achievement', stat: 'kills', goal: 500, coins: 1000, icon: '🗡️' },
  ach_pct_10: { kind: 'achievement', stat: 'bestPct', goal: 10, coins: 120, icon: '📈' },
  ach_pct_25: { kind: 'achievement', stat: 'bestPct', goal: 25, coins: 300, icon: '🏔️' },
  ach_pct_50: { kind: 'achievement', stat: 'bestPct', goal: 50, coins: 800, icon: '👑' },
  ach_stars_12: { kind: 'achievement', stat: 'stars', goal: 12, coins: 200, icon: '⭐' },
  ach_stars_36: { kind: 'achievement', stat: 'stars', goal: 36, coins: 500, icon: '🌟' },
  ach_stars_72: { kind: 'achievement', stat: 'stars', goal: 72, coins: 1500, icon: '💫' },
  ach_level_5: { kind: 'achievement', stat: 'level', goal: 5, coins: 100, icon: '🎖️' },
  ach_level_15: { kind: 'achievement', stat: 'level', goal: 15, coins: 400, icon: '🏅' },
  ach_streak_7: { kind: 'achievement', stat: 'streak', goal: 7, coins: 300, icon: '📅' },
  ach_matches_50: { kind: 'achievement', stat: 'matches', goal: 50, coins: 250, icon: '🎮' },
  ach_megas_10: { kind: 'achievement', stat: 'megas', goal: 10, coins: 200, icon: '💥' },
  ach_minions_25: { kind: 'achievement', stat: 'minions', goal: 25, coins: 200, icon: '🫧' },
  // secrets (easter eggs) — hints are deliberately vague in the UI
  egg_konami: { kind: 'secret', coins: 300, skin: 20, icon: '🌈' },
  egg_mascot_tap: { kind: 'secret', coins: 150, icon: '💃' },
  egg_lucky7: { kind: 'secret', coins: 77, icon: '🍀' },
  egg_pumpkin: { kind: 'secret', coins: 100, skin: 22, month: 10, icon: '🎃' },
  egg_night_owl: { kind: 'secret', coins: 100, icon: '🦉' },
  egg_quilly: { kind: 'secret', coins: 120, icon: '🪶' },
  egg_corners: { kind: 'secret', coins: 150, icon: '🧭' },
  egg_blot_whisperer: { kind: 'secret', coins: 120, icon: '🫥' },
  egg_speedrun: { kind: 'secret', coins: 150, icon: '⏱️' },
  egg_logo_spin: { kind: 'secret', coins: 50, icon: '🌀' },
  egg_ink_rain: { kind: 'secret', coins: 50, icon: '🌧️' },
  egg_hunter: { kind: 'secret', coins: 500, skin: 21, icon: '🏆' },
};

export const SECRET_IDS = Object.keys(FLAGS).filter((k) => FLAGS[k].kind === 'secret');
export const ACHIEVEMENT_IDS = Object.keys(FLAGS).filter((k) => FLAGS[k].kind === 'achievement');
/** Finding this many other secrets unlocks egg_hunter. */
export const EGG_HUNTER_GOAL = 5;

// ---------------------------------------------------------------- Daily Challenge

export type ChallengeModifier = 'giantBoss' | 'swarm' | 'quickFuse' | 'speedy' | 'hunters' | 'oneLife';
export const CHALLENGE_MODIFIERS: ChallengeModifier[] = ['giantBoss', 'swarm', 'quickFuse', 'speedy', 'hunters', 'oneLife'];

/** One seeded stage per UTC day — identical for every player, ranked on its own leaderboard. */
export function dailyChallenge(day: string): StageDef & { modifiers: ChallengeModifier[] } {
  let h = 2166136261;
  for (const c of day) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  const realmIndex = h % REALMS.length;
  const index = (h >>> 8) % STAGES_PER_REALM;
  const base = buildStage(realmIndex, index);
  const a = CHALLENGE_MODIFIERS[(h >>> 12) % CHALLENGE_MODIFIERS.length];
  let b = CHALLENGE_MODIFIERS[(h >>> 16) % CHALLENGE_MODIFIERS.length];
  if (b === a) b = CHALLENGE_MODIFIERS[(CHALLENGE_MODIFIERS.indexOf(a) + 1) % CHALLENGE_MODIFIERS.length];
  const modifiers = [a, b];
  const st: StageDef & { modifiers: ChallengeModifier[] } = {
    ...base,
    id: `daily-${day}`,
    targetPct: 0.78,
    bossSpeed: 9,
    minions: 4,
    hunters: 1,
    fuseDelay: 2.6,
    fuseSpeed: BASE_SPEED * 0.6,
    parTime: 120,
    seed: h,
    modifiers,
  };
  for (const m of modifiers) {
    if (m === 'giantBoss') st.bossRadius = 3.4;
    if (m === 'swarm') st.minions += 4;
    if (m === 'quickFuse') st.fuseDelay *= 0.6;
    if (m === 'speedy') st.bossSpeed *= 1.3;
    if (m === 'hunters') st.hunters += 2;
    if (m === 'oneLife') st.lives = 1;
  }
  return st;
}
